import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
	useUpdateTeamsInfoMutation,
	useLazyGetTeamsQuery,
	useLazyGetTeamsChannelsQuery,
	useDisconnectTeamsMutation,
	useLazyGetTeamsConnectionStatusQuery,
} from '../../../features/apis/intergration';
import useCurrentUser from '../../../customHook/useCurrentUser';
import ReactToastify from '../../../customHook/useReactToastify';
import { FaArrowRightLong } from 'react-icons/fa6';
import TeamsIcon from '../../../assets/images/microsoft-teams.svg';
import SelectSkeleton from '../SelectSkeleton';

interface Channel {
	id: string;
	name: string;
}

interface Team {
	id: string;
	displayName: string;
}

const TeamsSelect: React.FC = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const [updateTeamsInfo, { isLoading }] = useUpdateTeamsInfoMutation();
	const [getTeams, { data: teamsData }] = useLazyGetTeamsQuery();
	const [getChannels, { data: channelsData }] =
		useLazyGetTeamsChannelsQuery();
	const [disconnectTeams] = useDisconnectTeamsMutation();
	const [
		getConnectionStatus,
	] = useLazyGetTeamsConnectionStatusQuery();
	const user = useCurrentUser();
	const [selectedTeam, setSelectedTeam] = useState<string>('');
	const [selectedChannel, setSelectedChannel] = useState<string>('');
	const [isLoadingTeams, setIsLoadingTeams] = useState(false);

	const params = new URLSearchParams(location.search)
	const companyId = params.get('companyId') || user?.companyId || ''

	useEffect(() => {
		if (companyId) {
			getConnectionStatus(companyId)
				.unwrap()
				.then((res: any) => {
					if (res?.teamId && res?.channelId) {
						setSelectedTeam(res.teamId)
						setSelectedChannel(res.channelId)
					}
				})
				.catch(() => {})
		}
	}, [companyId, getConnectionStatus])

	useEffect(() => {
		if (companyId && !selectedTeam) {
			setIsLoadingTeams(true)
			getTeams(companyId)
				.unwrap()
				.then((res: any) => {
					if (res?.teams?.length > 0) {
						setSelectedTeam(res.teams[0].id)
					}
				})
				.catch(() => {})
				.finally(() => setIsLoadingTeams(false))
		}
	}, [companyId, selectedTeam, getTeams])

	useEffect(() => {
		if (selectedTeam && companyId) {
			getChannels({ companyId, teamId: selectedTeam })
		}
	}, [selectedTeam, companyId])

	const handleSubmit = async () => {
		if (!selectedChannel || !selectedTeam || !companyId) {
			ReactToastify({
				success: false,
				message: 'Channel or team missing',
			});
			return;
		}

		try {
			await updateTeamsInfo({
				companyId,
				data: {
					isTeams: true,
					teamId: selectedTeam,
					channel_id: selectedChannel,
				},
			}).unwrap();

			ReactToastify({
				success: true,
				message: 'Teams info updated successfully',
			});

			navigate('/dashboard/ask-tara');
		} catch (err) {
			console.error('Teams update failed:', err);
			ReactToastify({
				success: false,
				message: 'Something went wrong while saving Teams info',
			});
		}
	};

	const handleDisconnect = async () => {
		if (!companyId) return
		try {
			await disconnectTeams(companyId).unwrap()
			setSelectedTeam('')
			setSelectedChannel('')
			ReactToastify({ success: true, message: 'Teams disconnected' })
		} catch (err) {
			ReactToastify({ success: false, message: 'Failed to disconnect Teams' })
		}
	}

  return (
		<div className="relative w-full h-screen  overflow-hidden font-inter">
			<div className="flex items-center justify-center flex-col h-full">
				<div className="bg-white w-3/6  h-auto min-h-72 p-10 shadow-lg rounded-md border-[0.5px] border-[#eae8e8]">
					<div className="flex items-center justify-between mb-6">
						<div className="flex items-center gap-3">
							<img src={TeamsIcon} alt="Teams" className="h-8 w-8" />
							<h1 className="text-2xl font-bold">Microsoft Teams</h1>
						</div>
						<button
							type="button"
							onClick={handleDisconnect}
							className="px-3 py-1.5 text-red-600 border border-red-600 rounded hover:bg-red-50 transition text-sm"
						>
							Disconnect
						</button>
					</div>

					{isLoadingTeams || !teamsData ? (
						<SelectSkeleton />
					) : (
						<>
							<div>
								<label className="mb-2 text-xs font-roboto pl-2 block">
									Select Team
								</label>
								<select
									value={selectedTeam}
									onChange={(e) => setSelectedTeam(e.target.value)}
									className="w-full px-4 py-3 border-[0.5px] border-[#E2E2E2] rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-[#E2E2E2] text-sm text-gray-700 bg-white"
								>
									{teamsData?.teams?.map((team: Team) => (
										<option key={team.id} value={team.id}>
											{team.displayName}
										</option>
									))}
								</select>
							</div>

							<div>
								<label className="mb-2 text-xs font-roboto pl-2 block">
									Select Channel
								</label>
								<select
									value={selectedChannel}
									onChange={(e) => setSelectedChannel(e.target.value)}
									className="w-full px-4 py-3 border-[0.5px] border-[#E2E2E2] rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-[#E2E2E2] text-sm text-gray-700 bg-white"
								>
									<option value="">Select a Channel</option>
									{channelsData?.channels?.map((ch: Channel) => (
										<option key={ch.id} value={ch.id}>
											{ch.name}
										</option>
									))}
						</select>
							</div>

							<div className="flex justify-end items-center mt-4">
								<button
									type="button"
									onClick={handleSubmit}
									disabled={isLoading || !selectedChannel}
									className="bg-gradient-to-r from-[#6E3EF5] to-[#A818D8] hover:from-[#6E3EF5]/80 hover:to-[#A818D8]/80 flex items-center justify-center p-2 font-inter rounded-md text-white gap-2 hover:scale-105 hover:shadow text-xs md:text-sm add-my-team-btn disabled:opacity-50 disabled:cursor-not-allowed"
								>
									{isLoading ? 'Saving...' : 'Submit'} <FaArrowRightLong />
								</button>
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
};

export default TeamsSelect;
